import React from "react";
import { TextField, Checkbox, FormControlLabel } from "@mui/material";
import {
  Controller,
  type Control,
  type FieldValues,
  type Path,
} from "react-hook-form";
import { format } from "date-fns";
import type { AdditionalFieldType } from "@/types/additionalFields";
import {
  preventNonNumericValueWhenKeyDown,
  preventNonNumericValueWhenPaste,
} from "@/utils/utils";

interface AdditionalFieldInputProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  fieldType: AdditionalFieldType;
  disabled?: boolean;
  maxLength?: number;
}

/**
 * React Hook Formと統合された追加項目入力コンポーネント
 *
 * field_typeに応じた適切な入力UIをレンダリングします。
 * React Hook Formの`Controller`でラップされており、フォーム全体の状態管理に統合されます。
 *
 * @remarks
 * フォーム外で使用する場合（インライン編集、フィルター入力など）は、
 * 代わりに`renderAdditionalFieldInput`ユーティリティ関数を使用してください。
 *
 * @param control - React Hook FormのControlオブジェクト
 * @param name - フォーム内のフィールド名（例: "additionalFields.email"）
 * @param label - フィールドのラベル
 * @param fieldType - フィールドタイプ（text, textarea, number, date, datetime, boolean）
 * @param disabled - 入力を無効化するかどうか
 *
 * @see {@link renderAdditionalFieldInput} - React Hook Form不使用時のユーティリティ関数
 */
export function CAdditionalFieldInput<T extends FieldValues>({
  control,
  name,
  label,
  fieldType,
  disabled = false,
  maxLength = 500,
}: AdditionalFieldInputProps<T>): JSX.Element {
  return (
    <Controller
      name={name}
      control={control}
      rules={
        fieldType === "number"
          ? {
              validate: (value) => {
                if (!value || value === "") return true;
                const numStr = String(value).replace(/^-/, "");
                if (numStr.length > 15) {
                  return `${label}は15桁以内で入力してください`;
                }
                return true;
              },
            }
          : undefined
      }
      render={({ field, fieldState: { error } }) => {
        switch (fieldType) {
          case "text":
            return (
              <TextField
                {...field}
                fullWidth
                label={label}
                placeholder={`${label}を入力`}
                variant="outlined"
                disabled={disabled}
                inputProps={{ maxLength }}
                error={!!error}
                helperText={error?.message}
              />
            );

          case "textarea":
            return (
              <TextField
                {...field}
                fullWidth
                label={label}
                placeholder={`${label}を入力`}
                variant="outlined"
                multiline
                rows={3}
                disabled={disabled}
                inputProps={{ maxLength }}
                error={!!error}
                helperText={error?.message}
              />
            );

          case "number":
            return (
              <TextField
                {...field}
                fullWidth
                label={label}
                placeholder={`${label}を入力`}
                variant="outlined"
                type="number"
                disabled={disabled}
                error={!!error}
                helperText={error?.message}
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
                onKeyDown={preventNonNumericValueWhenKeyDown}
                onPaste={preventNonNumericValueWhenPaste}
                inputProps={{
                  // step="any"により小数点入力を許可
                  step: "any",
                }}
              />
            );

          case "date":
            return (
              <TextField
                {...field}
                fullWidth
                label={label}
                variant="outlined"
                type="date"
                disabled={disabled}
                InputLabelProps={{
                  shrink: true,
                }}
                value={
                  field.value
                    ? format(new Date(String(field.value)), "yyyy-MM-dd")
                    : ""
                }
                onChange={(e) => {
                  // 空の場合は空文字列として保存
                  field.onChange(e.target.value || "");
                }}
              />
            );

          case "datetime":
            return (
              <TextField
                {...field}
                fullWidth
                label={label}
                variant="outlined"
                type="datetime-local"
                disabled={disabled}
                InputLabelProps={{
                  shrink: true,
                }}
                value={
                  field.value
                    ? format(
                        new Date(String(field.value)),
                        "yyyy-MM-dd'T'HH:mm"
                      )
                    : ""
                }
                onChange={(e) => {
                  // 空の場合は空文字列として保存
                  field.onChange(e.target.value || "");
                }}
              />
            );

          case "boolean":
            return (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value === "true" || field.value === true}
                    onChange={(e) => {
                      // チェックボックスの値を文字列として保存
                      field.onChange(e.target.checked ? "true" : "false");
                    }}
                    disabled={disabled}
                  />
                }
                label={label}
              />
            );

          default:
            return (
              <TextField
                {...field}
                fullWidth
                label={label}
                placeholder={`${label}を入力`}
                variant="outlined"
                disabled={disabled}
                inputProps={{ maxLength }}
              />
            );
        }
      }}
    />
  );
}
